import React, { useState, useEffect, useRef } from 'react';
import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ChevronLeft, TrendingUp, Zap, Target,
  Play, Pause, Square, Activity, RotateCcw,
  Settings, AlertCircle, Clock, RefreshCw, Plus, Trash2, Loader2, X,
  Archive, ArchiveRestore, Repeat, PlayCircle, HelpCircle, ChevronDown, ChevronUp, Copy,
  /** Added for TP 설정 UI */
  Info
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAppSelector } from '@/store/hooks';

// Refactored Components
import { calculateFirstEntryNotional, fmtDate, fmtPriceForSymbol, getExchangeBadgeStyle } from './dca/DcaUtils';
import { FinanceItem, PriceBox } from './dca/FinanceItem';
import { PositionCard } from './dca/PositionCard';
import { LogsPanel } from './dca/LogsPanel';
import { SettingsPanel } from './dca/SettingsPanel';
import { LadderView } from './dca/LadderView';
import { formatPrice } from "@/lib/utils";
import { CycleHistoryPanel } from './dca/CycleHistoryPanel';
import { TradeHistoryPanel } from './dca/TradeHistoryPanel';
import { TradingChart } from './dca/TradingChart';
import { cx } from 'class-variance-authority';
import { useLatestValid } from '../hooks/useLatestValid';

interface BotDetailProps {
  bot: any;
  onBack: () => void;
  is_testnet?: boolean;
  disable_all_testnet?: boolean;
  enabled_exchanges?: Record<string, boolean>;
  testnet_min_notional?: number;
  onClone?: (bot: any) => void;
}

export const BotDetail: React.FC<BotDetailProps> = ({ bot: initialBot, onBack, is_testnet, disable_all_testnet, enabled_exchanges, testnet_min_notional, onClone }) => {
  const isPrivate = useAppSelector((state) => state.ui.isPrivate);
  const theme = useAppSelector((state) => state.ui.theme);
  const [bot, setBot] = useState(initialBot);
  const [activeTab, setActiveTab] = useState('overview');
  const [isLiquidating, setIsLiquidating] = useState(false);
  const [isLiquidatingSide, setIsLiquidatingSide] = useState(false);
  const [lastDismissedMessage, setLastDismissedMessage] = useState<string | null>(null);

  const [isLiquidateModalOpen, setIsLiquidateModalOpen] = useState(false);
  const [isControlPanelOpen, setIsControlPanelOpen] = useState(false);
  const [liquidateSide, setLiquidateSide] = useState<string | null>(null);
  const [liquidatePercent, setLiquidatePercent] = useState(100);
  const [shouldSuspend, setShouldSuspend] = useState(false);
  const [openHelpId, setOpenHelpId] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [customStopLoss, setCustomStopLoss] = useState('');
  const [customTpPct, setCustomTpPct] = useState('');
  const [modalTab, setModalTab] = useState('control');
  const modalRef = useRef<HTMLDivElement>(null);

  // Props가 외부에서 변경되었을 때(예: 목록에서 변경되어 들어올 때) 로컬 상태 동기화
  useEffect(() => {
    if (initialBot) setBot(initialBot);
  }, [initialBot]);

  if (!bot) return null;

  const currentPrice = Number(bot.current_price || 0);
  const activeDetails = bot.active_cycles_details || [];
  const leverage = Number(bot.leverage || 1);
  const firstEntryNotional = calculateFirstEntryNotional(bot);
  const anyPriceMissing = activeDetails.some((c: any) => c.unrealized_pnl === null || c.unrealized_pnl === undefined);
  const rawUnrealizedPnl = anyPriceMissing ? null : activeDetails.reduce((sum: number, c: any) => sum + Number(c.unrealized_pnl || 0), 0);
  const totalMargin = activeDetails.reduce((sum: number, c: any) => sum + Number(c.total_cost || 0), 0);
  const rawLeveragedRoe = (anyPriceMissing || totalMargin <= 0) ? null : (rawUnrealizedPnl! / totalMargin * 100);

  const [unrealizedPnl, isPnlStale] = useLatestValid(rawUnrealizedPnl);
  const [leveragedRoe, isRoeStale] = useLatestValid(rawLeveragedRoe);

  const isLong = (bot.side || 'long').toLowerCase() === 'long';
  const isActive = bot.is_active;
  const totalPnl = Number(bot.total_pnl || 0);
  const allocatedSeed = Number(bot.balance || 0);
  const totalFees = Number(bot.total_fees || 0);

  const currency = (bot.symbol?.includes('USDT') || bot.symbol?.includes('USD')) ? 'USDT' : 'KRW';
  const curSym = currency === 'USDT' ? '$' : '₩';

  const slPrice = Number(bot.sl_price || 0);
  const tpPrice = Number(bot.tp_price || 0);

  // 전체 청산 로딩 상태 (버튼 라벨용)
  const isTotalLiquidating = isLiquidating || (bot.status_message && bot.status_message.includes('전체') && bot.status_message.includes('청산') && bot.status_message.includes('처리 중'));

  // 개별/전체 포함 어떠한 청산이라도 진행 중인지 여부 (버튼 비활성화용)
  const isAnyLiquidating = isTotalLiquidating || isLiquidatingSide || (bot.status_message && bot.status_message.includes('청산') && bot.status_message.includes('처리 중'));

  const handleStop = () => { if (window.confirm('봇을 정지하시겠습니까?')) router.post(`/dca/${bot.id}/stop`, {}, { preserveScroll: true }); };
  const handleStart = () => { if (window.confirm('봇을 가동하시겠습니까?')) router.post(`/dca/${bot.id}/start`, {}, { preserveScroll: true }); };
  const handleReset = () => { if (window.confirm('초기화하시겠습니까?')) router.post(`/dca/${bot.id}/reset`, {}, { preserveScroll: true }); };
  const handleLiquidate = () => {
    if (window.confirm('전체 포지션을 시장가 청산하시겠습니까?')) {
      router.post(`/dca/${bot.id}/liquidate`, {}, {
        preserveScroll: true,
        onStart: () => setIsLiquidating(true),
        onFinish: () => setIsLiquidating(false)
      });
    }
  };
  const handleDeposit = () => { const amt = window.prompt('보강할 시드 금액을 입력하세요:'); if (amt) router.post(`/dca/${bot.id}/deposit`, { amount: amt }, { preserveScroll: true }); };
  const handleWithdraw = () => { const amt = window.prompt('인출할 수익 금액을 입력하세요:'); if (amt) router.post(`/dca/${bot.id}/withdraw`, { amount: amt }, { preserveScroll: true }); };
  const handleToggleDisabled = () => { router.post(`/dca/${bot.id}/toggle_disabled`, {}, { preserveScroll: true }); };
  const handleDelete = () => { if (window.confirm('봇을 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) router.delete(`/dca/${bot.id}`, { onSuccess: () => onBack() }); };
  const handleArchive = () => {
    if (window.confirm('봇을 보관하시겠습니까? 목록에서 숨겨집니다.')) {
      router.post(`/dca/${bot.id}/archive`, {}, {
        preserveScroll: true,
        onSuccess: () => {
          router.reload({
            only: ['initial_bots'],
            onSuccess: () => onBack()
          });
        }
      });
    }
  };
  const handleUnarchive = () => {
    router.post(`/dca/${bot.id}/unarchive`, {}, {
      preserveScroll: true,
      onSuccess: () => {
        router.reload({ only: ['bot'] });
      }
    });
  };
  const handleManualEntry = (side: string) => {
    if (window.confirm(`${side.toUpperCase()} 방향으로 즉시 수동 진입하시겠습니까?`)) {
      router.post(`/dca/${bot.id}/force_entry`, { side }, {
        preserveScroll: true,
        onStart: () => setActionPending(true),
        onFinish: () => setActionPending(false)
      });
    }
  };
  const handleManageSide = (side: string) => {
    setLiquidateSide(side);
    setShouldSuspend(false);
    setCustomStopLoss(bot.settings?.[`${side.toLowerCase()}_stop_loss_pct`]?.toString() || '');
    setModalTab('control');
    setIsLiquidateModalOpen(true);
  };
  const handleUpdateCustomSl = () => {
    if (!liquidateSide) return;
    router.post(`/dca/${bot.id}/update_custom_sl`, {
      side: liquidateSide,
      stop_loss_pct: customStopLoss
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setIsLiquidateModalOpen(false);
        router.reload({ only: ['bot'] });
      }
    });
  };
  const handleUpdateCustomTp = () => {
    if (!liquidateSide) return;
    router.post(`/dca/${bot.id}/update_custom_sl`, {
      side: liquidateSide,
      tp_pct_override: customTpPct
    }, {
      preserveScroll: true,
      onSuccess: () => {
        setIsLiquidateModalOpen(false);
        router.reload({ only: ['bot'] });
      }
    });
  };
  const handleResumeAll = (side: string) => {
    router.post(`/dca/${bot.id}/resume_all`, { side }, { preserveScroll: true });
  };
  const handlePauseBuy = (side: string) => {
    router.post(`/dca/${bot.id}/pause_buy`, { side }, { preserveScroll: true });
  };
  const handleToggleStopAtTp = (side: string) => {
    router.post(`/dca/${bot.id}/toggle_stop_at_tp`, { side }, { preserveScroll: true });
  };

  const confirmLiquidateSide = () => {
    if (!liquidateSide) return;

    // 100%일 때는 기존 liquidate_side 호출, 그 외에는 liquidate_partial 호출
    const endpoint = liquidatePercent >= 100 ? 'liquidate_side' : 'liquidate_partial';
    const payload = liquidatePercent >= 100 ? { side: liquidateSide, suspend: shouldSuspend } : { side: liquidateSide, percent: liquidatePercent };

    router.post(`/dca/${bot.id}/${endpoint}`, payload, {
      preserveScroll: true,
      onStart: () => setIsLiquidatingSide(true),
      onFinish: () => {
        setIsLiquidatingSide(false);
        setIsLiquidateModalOpen(false);
        setLiquidatePercent(100);
        router.reload({ only: ['bot'] });
      }
    });
  };

  const handleResumeSide = (side: string) => {
    router.post(`/dca/${bot.id}/resume_side`, { side }, {
      preserveScroll: true,
      onStart: () => setActionPending(true),
      onFinish: () => setActionPending(false)
    });
  };
  const handleSuspendSide = (side: string) => {
    //if (window.confirm(`${side.toUpperCase()} 방향의 신규 진입을 일시 중단하시겠습니까?`)) {
    router.post(`/dca/${bot.id}/suspend_side`, { side }, {
      preserveScroll: true,
      onStart: () => setActionPending(true),
      onFinish: () => setActionPending(false)
    });
    //}
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Button variant="ghost" onClick={onBack} className="text-muted-foreground hover:text-foreground font-bold p-0 pr-4">
          <ChevronLeft className="w-5 h-5 mr-1" /> 봇 목록으로
        </Button>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { router.reload(); }} className="text-muted-foreground p-1.5"><RefreshCw className="w-4 h-4" /></Button>
          {bot.disabled && <Badge className="bg-danger/10 text-danger border-none font-bold px-3 py-1">Disabled</Badge>}
          {isLiquidating && <Badge className="bg-amber-500/10 text-amber-500 border-none font-bold px-3 py-1 animate-pulse"><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> 청산 진행 중</Badge>}
          {isActive ? <Badge className="bg-success/10 text-success border-none font-bold px-3 py-1 animate-pulse">운용 중</Badge> : <Badge className="bg-muted text-muted-foreground border-none font-bold px-3 py-1">정지됨</Badge>}
        </div>
      </div>

      {bot.is_live && bot.use_testnet && (bot.exchange === 'binance' || !bot.exchange) && firstEntryNotional < (testnet_min_notional || 100) && (
        <Alert className="bg-danger/10 border-danger/20 text-danger rounded-2xl">
          <AlertCircle className="w-4 h-4" />
          <AlertTitle className="text-xs font-black">바이낸스 테스트넷 최소 주문 금액 미달</AlertTitle>
          <AlertDescription className="text-[11px] font-bold opacity-80">
            현재 첫 진입 규모({firstEntryNotional.toFixed(2)} USDT)가 최소 주문 제한({testnet_min_notional || 100} USDT)보다 작습니다. (레버리지 포함 가치 기준)
          </AlertDescription>
        </Alert>
      )}

      {bot.status_message && (bot.status_message.includes('청산') || bot.status_message.includes('요청') || bot.status_message.includes('정지') || bot.status_message.includes('제한') || bot.status_message.includes('억제') || bot.status_message.includes('🚫')) && bot.status_message !== lastDismissedMessage && (
        <Alert className={`${(bot.status_message.includes('완료') && !bot.status_message.includes('🚫')) ? 'bg-success/10 border-success/20 text-success' : (bot.status_message.includes('🚫') ? 'bg-danger/10 border-danger/20 text-danger' : 'bg-amber-500/10 border-amber-500/20 text-amber-600')} rounded-2xl relative overflow-hidden pr-10`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLastDismissedMessage(bot.status_message)}
            className="absolute right-2 top-3 h-8 w-8 p-0 text-muted-foreground hover:text-foreground hover:bg-black/5 rounded-full"
          >
            <X className="w-4 h-4" />
          </Button>
          {bot.status_message.includes('🚫') ? <AlertCircle className="w-4 h-4" /> : (bot.status_message.includes('완료') ? <Activity className="w-4 h-4" /> : <Loader2 className="w-4 h-4" />)}
          <AlertTitle className="text-xs font-black">
            {bot.status_message.includes('🚫') ? '가동 차단됨 (Conflict)' : (bot.status_message.includes('완료') ? '처리 완료' : '명령 처리 중')}
          </AlertTitle>
          <AlertDescription className="text-[11px] font-bold opacity-80">
            {bot.status_message}
            {(!bot.status_message.includes('완료') && !bot.status_message.includes('🚫')) && ' (엔진의 처리를 기다리는 중입니다...)'}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-12">
          <Card className="rounded-3xl border border-border shadow-sm bg-card overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 divide-x divide-border font-sans">
              <FinanceItem label={bot.use_allocated_balance ? "배정 원금" : "운용 모드"} value={bot.use_allocated_balance ? `${fmtPriceForSymbol(allocatedSeed, bot.symbol)} ${currency}` : "전체 계좌"} sub={bot.use_allocated_balance ? "Seed Money (Cost)" : "Full Portfolio"} icon={<Target className="w-3.5 h-3.5" />} isPrivate={isPrivate} />
              <FinanceItem label="누적 수익" value={`${totalPnl >= 0 ? '+' : ''}${formatPrice(totalPnl, 2)} ${currency}`} sub={'Profit'} icon={<TrendingUp className="w-3.5 h-3.5" />} color={totalPnl >= 0 ? 'text-success' : 'text-danger'} isPrivate={isPrivate} />
              <FinanceItem label="합산 미실현 수익"
                value={unrealizedPnl === null ? '—' : `${unrealizedPnl >= 0 ? '+' : ''}${formatPrice(unrealizedPnl, 2)} ${currency}`}
                sub={leveragedRoe === null ? '—' : `${leveragedRoe.toFixed(2)}% (PNL)`}
                icon={<Activity className="w-3.5 h-3.5" />}
                color={unrealizedPnl === null ? 'text-muted-foreground/30' : (unrealizedPnl >= 0 ? 'text-success' : 'text-danger')}
                className={isPnlStale || isRoeStale ? 'opacity-40' : ''}
                isPrivate={isPrivate}
              />
              <FinanceItem label="거래 횟수" value={`${bot.trades_count || 0}회`} sub="Trade Count" icon={<RefreshCw className="w-3.5 h-3.5" />} color="text-amber-500" />
              <FinanceItem label="누적 수수료" value={`${formatPrice(totalFees, 2)} ${currency}`} sub="Commission" icon={<RotateCcw className="w-3.5 h-3.5" />} color="text-muted-foreground" isPrivate={isPrivate} />
              <FinanceItem label="시작 시간" value={fmtDate(bot.created_at)} sub="전략 생성 시간" icon={<Clock className="w-3.5 h-3.5" />} />
              <FinanceItem label="추정 총 자산"
                value={(unrealizedPnl === null) ? '—' : `${formatPrice(allocatedSeed + totalPnl + unrealizedPnl - totalFees, 2)} ${currency}`}
                sub="Estimated Equity (Cost)"
                icon={<Zap className="w-3.5 h-3.5 text-blue-500" />}
                highlight
                className={isPnlStale ? 'opacity-40' : ''}
                isPrivate={isPrivate}
              />
            </div>
          </Card>
        </div>

        <div className="md:col-span-8 space-y-6">
          <Card className="rounded-3xl border-none shadow-xl overflow-hidden bg-card transition-all">
            <CardHeader className={`p-6 border-t-4 ${bot.side?.toUpperCase() === 'BOTH' ? 'bg-primary/5 border-primary' : (isLong ? 'bg-success/5 border-success' : 'bg-danger/5 border-danger')}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex flex-col gap-0.5 mb-1">
                    <p className="text-[10px] md:text-xs font-bold text-muted-foreground uppercase tracking-wider">
                      #{bot.id} {bot.name || 'Unnamed Bot'} • 거래 {bot.trades_count || 0}회
                    </p>
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl md:text-3xl font-black text-foreground">{bot.symbol}</h2>
                      <Badge className={`border-none text-[9px] md:text-[11px] font-bold ${bot.side?.toUpperCase() === 'BOTH' ? 'bg-indigo-500/10 text-indigo-500' : (isLong ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger')}`}>{bot.side?.toUpperCase() === 'BOTH' ? 'Both' : (isLong ? 'Long' : 'Short')} {leverage}x</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-[8px] md:text-[10px] text-muted-foreground font-medium uppercase tracking-widest">Real-time Strategy Monitoring</p>
                    <Badge variant="outline" className={`text-[9px] font-black h-4 px-1.5 leading-none uppercase ${bot.is_live ? 'bg-primary/10 text-primary border-primary/20' : 'bg-amber-500/10 text-amber-600 border-amber-600/20'}`}>{bot.is_live ? 'Live' : 'Paper'}</Badge>
                    {bot.is_live && bot.use_testnet && (
                      <Badge variant="outline" className="bg-indigo-500/10 text-indigo-500 border-indigo-500/20 text-[9px] font-black h-4 px-1.5 leading-none uppercase">Testnet</Badge>
                    )}
                    <Badge variant="secondary" className={`text-[9px] font-black h-4 px-1.5 leading-none uppercase ${getExchangeBadgeStyle(bot.exchange)}`}>
                      {bot.exchange || 'binance'}
                    </Badge>
                    <Badge variant="outline" className={`text-[9px] font-black h-4 px-1.5 gap-1 leading-none uppercase ${bot.cyclic ? 'bg-primary/5 text-primary border-primary/20' : 'bg-muted text-muted-foreground border-border'}`}>
                      {bot.cyclic ? <Repeat className="w-2 h-2" /> : <PlayCircle className="w-2 h-2" />}
                      {bot.cyclic ? 'Repeat' : 'Once'}
                    </Badge>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-muted-foreground font-bold uppercase mb-0.5">실시간 시세</p>
                  <p className="text-xl md:text-3xl font-black text-foreground tabular-nums">{curSym}{fmtPriceForSymbol(currentPrice, bot.symbol)}</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <div className={cx(
                  "grid grid-cols-1 gap-4",
                  (activeDetails.length > 1 || (bot.side?.toUpperCase() === 'BOTH' && activeDetails.length <= 1)) ? "lg:grid-cols-2" : ""
                )}>
                  {activeDetails.map((c: any) => {
                    const side = c.side.toLowerCase();
                    const getStatusPart = () => {
                      if (!bot.status_message) return null;
                      const parts = bot.status_message.split('|');
                      const sidePrefix = side.toUpperCase() + ':';
                      const part = parts.find((p: string) => p.trim().startsWith(sidePrefix));
                      if (!part) {
                        // 파이프 구분자가 없는 단일 메시지: 상대편 side 언급이 있으면 표시 안 함
                        const otherSide = side === 'long' ? 'SHORT' : 'LONG';
                        if (bot.status_message.includes(otherSide) && !bot.status_message.includes(side.toUpperCase())) {
                          return null;
                        }
                        return bot.status_message;
                      }
                      return part.split(sidePrefix)[1]?.trim();
                    };

                    return (
                      <PositionCard
                        key={c.id}
                        bot={bot}
                        cycle={c}
                        leverage={leverage}
                        curSym={curSym}
                        onManage={handleManageSide}
                        isPaused={bot.paused_buy_sides?.includes(c.side)}
                        disabled={!isActive || isAnyLiquidating}
                        isPrivate={isPrivate}
                        statusMessage={getStatusPart() || undefined}
                      />
                    );
                  })}

                  {isActive && ['long', 'short'].filter(s =>
                    (bot.side?.toUpperCase() === 'BOTH' || bot.side?.toLowerCase() === s) &&
                    !activeDetails.some((c: any) => c.side === s)
                  ).map(s => {
                    const isSuspended = bot.suspended_sides?.includes(s);
                    const getStatusPart = () => {
                      if (!bot.status_message) return null;
                      const parts = bot.status_message.split('|');
                      const sidePrefix = s.toUpperCase() + ':';
                      const part = parts.find((p: string) => p.trim().startsWith(sidePrefix));
                      if (!part) return null;
                      return part.split(sidePrefix)[1]?.trim();
                    };
                    const statusText = getStatusPart();

                    return (
                      <div key={s} className="bg-muted/5 border border-dashed border-border rounded-[24px] p-5 flex flex-col justify-between min-h-[140px] relative overflow-hidden group hover:border-primary/20 transition-all">
                        <div className="absolute top-0 right-0 p-3 opacity-[0.03] group-hover:opacity-[0.07] transition-all">
                          {s === 'long' ? <TrendingUp className="w-16 h-16" /> : <TrendingUp className="w-16 h-16 rotate-180" />}
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge variant="outline" className={cx("text-[10px] font-black px-2 py-0.5 rounded-lg border-none shadow-sm", (s === 'long' ? "bg-success/10 text-success" : "bg-danger/10 text-danger"))}>
                                {s.toUpperCase()}
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleManageSide(s)}
                                className="h-7 px-2.5 text-[10px] font-black bg-background hover:bg-muted/10 border-border rounded-lg transition-all flex items-center gap-1"
                              >
                                <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                                포지션 관리
                              </Button>
                            </div>
                            {isSuspended ? (
                              <Badge className="bg-amber-500/10 text-amber-500 border-none text-[9px] font-black px-2">진입 금지 (SUSPENDED)</Badge>
                            ) : (
                              <Badge className="bg-blue-500/10 text-blue-500 border-none text-[9px] font-black px-2 animate-pulse">진입 탐색 중 (SCANNING)</Badge>
                            )}
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-black text-foreground/80">
                              {isSuspended ? '재진입 방지 활성화됨' : '신규 진입 대기 중'}
                            </p>
                            <p className="text-[10px] font-bold text-muted-foreground leading-relaxed max-w-[90%]">
                              {statusText || (isSuspended ? '사용자 요청으로 인해 해당 방향의 신규 진입이 차단된 상태입니다.' : '전략 엔진이 최적의 진입 타점을 분석하고 있습니다.')}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-5">
                          {isSuspended ? (
                            <Button variant="outline" size="sm" onClick={() => handleResumeSide(s)} disabled={actionPending} className="bg-amber-500/10 text-amber-600 border-amber-500/20 hover:bg-amber-500/20 font-black h-8 px-4 text-[10px] rounded-xl shadow-sm">
                              <Play className="w-3 h-3 mr-1.5 fill-current" /> 진입 금지 해제
                            </Button>
                          ) : (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleManualEntry(s)}
                                disabled={actionPending}
                                className={cx(
                                  "font-black h-8 px-4 text-[10px] rounded-xl shadow-sm transition-all",
                                  s === 'long'
                                    ? "bg-success/[0.03] text-success/70 border-success/10 hover:bg-success/10 hover:text-success"
                                    : "bg-danger/[0.03] text-danger/70 border-danger/10 hover:bg-danger/10 hover:text-danger"
                                )}
                              >
                                <Plus className="w-3.5 h-3.5 mr-1" /> 수동 {s.toUpperCase()} 진입
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSuspendSide(s)}
                                disabled={actionPending}
                                className="font-black h-8 px-4 text-[10px] rounded-xl shadow-sm bg-amber-500/[0.03] text-amber-600/70 border-amber-500/10 hover:bg-amber-500/10 hover:text-amber-600 transition-all"
                              >
                                <Pause className="w-3.5 h-3.5 mr-1" /> 진입 일시정지
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!isActive && activeDetails.length === 0 && (
                  <div className="py-16 text-center bg-muted/5 rounded-[32px] border-2 border-dashed border-border flex flex-col items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-muted/10 flex items-center justify-center">
                      <Zap className="w-6 h-6 text-muted-foreground/30" />
                    </div>
                    <div>
                      <p className="text-base font-black text-foreground/70">봇 가동 중지됨</p>
                      <p className="text-xs font-bold text-muted-foreground mt-1">운용을 시작하려면 우측 상단의 가동 버튼을 눌러주세요.</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 pt-4 border-t border-border">
                <PriceBox label="최근 가격" value={`${curSym}${fmtPriceForSymbol(currentPrice, bot.symbol)}`} color="blue" isPrivate={isPrivate} />
                <PriceBox label="운용 상태" value={isActive ? 'RUNNING' : 'STOPPED'} color={isActive ? 'success' : 'slate'} sub={!isActive && bot.stopped_at ? fmtDate(bot.stopped_at) : undefined} />
                <PriceBox label="익절 모드" value={bot.settings?.use_limit_tp ? 'LIMIT' : 'MARKET'} color={bot.settings?.use_limit_tp ? 'success' : 'blue'} />
                <PriceBox label="레버리지" value={`${leverage}x`} color="slate" />
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-3xl border-none shadow-lg bg-card overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full rounded-none border-b border-border bg-transparent p-0 h-auto flex flex-wrap bg-muted/5">
                <TabsTrigger value="overview" className="flex-1 py-3 text-[10px] md:text-xs font-bold data-[state=active]:text-primary whitespace-nowrap">사다리 정보</TabsTrigger>
                <TabsTrigger value="cycles" className="flex-1 py-3 text-[10px] md:text-xs font-bold data-[state=active]:text-primary whitespace-nowrap">사이클 이력</TabsTrigger>
                <TabsTrigger value="trades" className="flex-1 py-3 text-[10px] md:text-xs font-bold data-[state=active]:text-primary whitespace-nowrap">체결 내역</TabsTrigger>
                <TabsTrigger value="logs" className="flex-1 py-3 text-[10px] md:text-xs font-bold data-[state=active]:text-primary whitespace-nowrap">로그</TabsTrigger>
                <TabsTrigger value="settings" className="flex-1 py-3 text-[10px] md:text-xs font-bold data-[state=active]:text-primary whitespace-nowrap">설정</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="mt-0"><LadderView activeDetails={activeDetails} leverage={leverage} curSym={curSym} botId={bot.id} /></TabsContent>
              <TabsContent value="settings" className="mt-0 p-6"><SettingsPanel bot={bot} onSaved={(updatedBot) => { if (updatedBot) { setBot(updatedBot); } else { router.reload({ only: ['initial_bots'] }); } }} is_testnet={is_testnet} disable_all_testnet={disable_all_testnet} enabled_exchanges={enabled_exchanges} testnet_min_notional={testnet_min_notional} onClone={onClone} /></TabsContent>
              <TabsContent value="logs" className="mt-0 p-4"><LogsPanel botId={bot.id} /></TabsContent>
              <TabsContent value="cycles" className="mt-0 p-4"><CycleHistoryPanel botId={bot.id} symbol={bot.symbol} /></TabsContent>
              <TabsContent value="trades" className="mt-0 p-4"><TradeHistoryPanel botId={bot.id} symbol={bot.symbol} leverage={bot.leverage} /></TabsContent>
            </Tabs>
          </Card>
        </div>

        <div className="md:col-span-4 space-y-6">
          <Card className="rounded-3xl border border-border shadow-sm bg-card p-6">
            <div
              className="flex justify-between items-center cursor-pointer select-none"
              onClick={() => setIsControlPanelOpen(!isControlPanelOpen)}
            >
              <h3 className="text-lg font-black text-foreground flex items-center gap-2">
                <Settings className="w-5 h-5 text-primary" /> 제어 패널
              </h3>
              {isControlPanelOpen ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
            </div>
            {isControlPanelOpen && (
              <div className="space-y-3 mt-4">
                {isActive ? (
                  <Button onClick={handleStop} variant="outline" className="w-full h-12 rounded-2xl border-danger/20 text-danger hover:bg-danger/10 font-black">
                    <Square className="w-4 h-4 mr-2 fill-danger" /> 봇 정지
                  </Button>
                ) : (
                  <Button
                    onClick={handleStart}
                    disabled={bot.disabled}
                    className={`w-full h-12 rounded-2xl font-black shadow-lg ${bot.disabled ? 'bg-muted text-muted-foreground cursor-not-allowed shadow-none' : 'bg-success hover:bg-success/90 text-white shadow-success/20'}`}
                  >
                    <Play className="w-4 h-4 mr-2" /> 봇 가동
                  </Button>
                )}
                <Button
                  onClick={handleLiquidate}
                  disabled={!isActive || activeDetails.length === 0 || isAnyLiquidating}
                  variant="outline"
                  className="w-full h-12 rounded-2xl font-black border-primary/20 text-primary bg-primary/[0.02] hover:bg-primary hover:text-white transition-all shadow-none"
                >
                  {isTotalLiquidating ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> 전체 청산 중...</> : <><Activity className="w-4 h-4 mr-1.5" /> 전 포지션 시장가 청산</>}
                </Button>
                {onClone && (
                  <Button
                    onClick={() => onClone(bot)}
                    variant="outline"
                    className="w-full h-12 rounded-2xl font-black border-primary/35 text-primary bg-primary/[0.01] hover:bg-primary/10 transition-all shadow-none flex items-center justify-center gap-2"
                  >
                    <Copy className="w-4 h-4" /> 설정 복제하여 신규 생성
                  </Button>
                )}
                <div className="pt-4 mt-2 border-t border-border space-y-2">
                  <Button onClick={handleReset} disabled={isActive} variant="ghost" className="w-full h-10 rounded-xl text-muted-foreground hover:text-amber-500 text-[11px] font-bold">
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> 가상 포지션 초기화 (Reset)
                  </Button>
                  {bot.archived ? (
                    <Button onClick={handleUnarchive} variant="ghost" className="w-full h-10 rounded-xl text-primary hover:text-primary/80 text-[11px] font-bold">
                      <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" /> 봇 보관 취소 (Unarchive)
                    </Button>
                  ) : (
                    <Button onClick={handleArchive} disabled={isActive} variant="ghost" className="w-full h-10 rounded-xl text-muted-foreground hover:text-primary text-[11px] font-bold">
                      <Archive className="w-3.5 h-3.5 mr-1.5" /> 봇 보관하기 (Archive)
                    </Button>
                  )}
                  <Button onClick={handleDelete} disabled={isActive} variant="ghost" className="w-full h-10 rounded-xl text-muted-foreground hover:text-danger text-[11px] font-bold">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> 봇 영구 삭제
                  </Button>
                </div>
                {false && !bot.is_live && (
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={handleDeposit} variant="outline" className="h-10 rounded-xl text-[11px] font-bold"><Plus className="w-3.5 h-3.5 mr-1.5" /> 시드 보강</Button>
                    <Button onClick={handleWithdraw} variant="outline" className="h-10 rounded-xl text-[11px] font-bold"><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> 수익 인출</Button>
                  </div>
                )}
              </div>
            )}
          </Card>

          <TradingChart
            symbol={bot.symbol}
            exchange={bot.exchange}
            isTestnet={bot.use_testnet}
            positions={bot.active_cycles_details}
            theme={theme}
          />

          {/* 💡 전략 엔진 종합 도움말 및 가이드 (아코디언 UI) */}
          <Card className="rounded-3xl border border-border shadow-sm bg-card overflow-hidden transition-all hover:shadow-md">
            <div className="p-6 pb-4 border-b border-border/50 bg-muted/[0.02]">
              <h3 className="text-base md:text-lg font-black text-foreground flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-primary" /> 전략 엔진 도움말 및 팁
              </h3>
              <p className="text-xs text-muted-foreground font-medium mt-1">DCA 봇의 핵심 방어 로직과 실전 세팅 팁을 확인하세요.</p>
            </div>

            <div className="divide-y divide-border/50">
              {[
                {
                  id: 'super_ma',
                  title: '📊 슈퍼 이평선 필터 (Super MA)',
                  summary: '4시간봉(4h) 기준 5개 EMA 추세 필터 메커니즘 안내',
                  content: (
                    <div className="p-6 pt-4 space-y-4 animate-in fade-in-50 duration-200 bg-muted/[0.01]">
                      <p className="text-xs text-muted-foreground font-medium leading-relaxed">
                        4시간봉(4h) 기준 5개 주요 지수 이동평균선(EMA 5, 10, 20, 60, 120)의 정배열 상태를 실시간 점수화(0~5점)하여, 시장 국면을 판별하고 위험 구간 진입을 차단하는 강력한 추세 필터입니다.
                      </p>

                      <div className="space-y-2.5">
                        <div className="flex items-start gap-3 p-3 rounded-2xl bg-success/5 border border-success/10 transition-all hover:bg-success/10">
                          <Badge className="bg-success text-white border-none font-black text-xs px-2.5 py-1 shadow-sm shadow-success/20">5점</Badge>
                          <div className="space-y-0.5">
                            <p className="text-xs font-black text-success-foreground">완벽한 정배열 (대세 추세장)</p>
                            <p className="text-[11px] text-muted-foreground font-medium leading-normal max-w-[95%]">단기, 중기, 장기 이평선이 모두 완벽하게 한 방향을 가리키는 강력한 상승/하락 국면입니다.</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3 p-3 rounded-2xl bg-primary/5 border border-primary/10 transition-all hover:bg-primary/10">
                          <Badge className="bg-primary text-white border-none font-black text-xs px-2.5 py-1 shadow-sm shadow-primary/20">3점</Badge>
                          <div className="space-y-0.5">
                            <p className="text-xs font-black text-primary">[기준점] 최소 안전 마진 확보 (단기/중기 추세 회복)</p>
                            <p className="text-[11px] text-muted-foreground font-medium leading-normal max-w-[95%]">장기 저항선을 완전히 뚫지 못했더라도, 최소한 단기 및 중기 이평선(5, 10, 20 EMA) 위로 주가가 올라와 지지선이 확인된 안전 구간입니다.</p>
                          </div>
                        </div>

                        <div className="flex items-start gap-3 p-3 rounded-2xl bg-danger/5 border border-danger/10 transition-all hover:bg-danger/10">
                          <Badge className="bg-danger text-white border-none font-black text-xs px-2.5 py-1 shadow-sm shadow-danger/20">1~2점</Badge>
                          <div className="space-y-0.5">
                            <p className="text-xs font-black text-danger">위험 및 역추세 구간 (진입 강제 차단)</p>
                            <p className="text-[11px] text-muted-foreground font-medium leading-normal max-w-[95%]">주가가 주요 이평선들 밑에 깔려 있어 하락 압력이 매우 강한 상태입니다. 떨어지는 칼날을 피하기 위해 신규 진입을 즉시 차단합니다.</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 p-3.5 bg-primary/[0.02] border border-primary/10 rounded-2xl flex items-start gap-2.5">
                        <Zap className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="text-xs font-black text-foreground">💡 실전 세팅 팁 (3점 vs 2점)</p>
                          <p className="text-[11px] text-muted-foreground font-bold leading-relaxed opacity-80">
                            <span className="text-primary font-black">3점 세팅(기본값):</span> 확실하게 지지선을 회복한 종목만 진입하여 승률과 시드 보호를 극대화합니다. <br />
                            <span className="text-amber-500 font-black">2점 세팅(스캘핑 튜닝):</span> 비트코인(BTC)처럼 무거운 횡보장에서도 발 빠르게 진입 기회를 늘리고 싶을 때 사용합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'offsetting',
                  title: '🛡 상계처리 (Profit Offsetting) 메커니즘',
                  summary: '반대 포지션 수익을 활용한 손실 포지션 축소 방어 로직',
                  content: (
                    <div className="p-6 pt-4 space-y-4 animate-in fade-in-50 duration-200 bg-muted/[0.01]">
                      <div className="space-y-2">
                        <h4 className="text-xs font-black text-foreground">📌 상계처리란?</h4>
                        <p className="text-xs text-muted-foreground font-medium leading-relaxed max-w-[95%]">
                          Both(양방향) 모드에서 한쪽 방향(예: SHORT)이 익절 수익을 낼 때, 그 수익의 일부를 활용하여 반대 방향(예: LONG)의 가장 손실이 큰 고점 물량(마지막 단계 물량)을 시장가로 덜어내는 고급 방어 기술입니다.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                        <div className="p-3.5 rounded-2xl bg-muted/5 border border-border">
                          <p className="text-xs font-black text-foreground mb-1 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-primary" /> 평단가 획기적 하향</p>
                          <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">가장 불리한 가격에 체결된 물량을 잘라내므로 전체 평균 단가가 크게 낮아져 탈출 기회를 앞당깁니다.</p>
                        </div>
                        <div className="p-3.5 rounded-2xl bg-muted/5 border border-border">
                          <p className="text-xs font-black text-foreground mb-1 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-500" /> 증거금 회수 및 청산 방어</p>
                          <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">묶여 있던 증거금을 즉시 회수하여 가용 잔고를 늘리고 강제 청산 위험을 크게 낮춥니다.</p>
                        </div>
                      </div>

                      <div className="mt-4 p-3.5 bg-primary/[0.02] border border-primary/10 rounded-2xl flex items-start gap-2.5">
                        <Zap className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="text-xs font-black text-foreground">💡 실전 세팅 팁 (비율 조절)</p>
                          <p className="text-[11px] text-muted-foreground font-bold leading-relaxed opacity-80">
                            봇 설정에서 <span className="text-primary font-black">상계처리 비율(Offset Ratio)</span>을 조절하여 공격적인 익절(수익 우선)과 보수적인 상계(방어 우선) 간의 밸런스를 자유롭게 맞출 수 있습니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'timeout_be',
                  title: '⏱ 4시간 본절청산 (Timeout Break-Even)',
                  summary: '고단계 진입 시 시드 보호를 위한 타임아웃 조기 탈출 기능',
                  content: (
                    <div className="p-6 pt-4 space-y-4 animate-in fade-in-50 duration-200 bg-muted/[0.01]">
                      <div className="space-y-2">
                        <h4 className="text-xs font-black text-foreground">📌 타임아웃 본절청산이란?</h4>
                        <p className="text-xs text-muted-foreground font-medium leading-relaxed max-w-[95%]">
                          진입 후 4시간이 지나도록 추세가 회복되지 않을 경우, 봇이 위험을 감지하고 목표 익절가를 기존 목표가에서 <span className="text-primary font-bold">'사용자가 설정한 ROE 기준 (예: 1.6%, 0% 등)'</span>으로 낮추어 조기 탈출을 시도하는 기능입니다.
                        </p>
                      </div>

                      <div className="p-3.5 rounded-2xl bg-danger/5 border border-danger/10">
                        <p className="text-xs font-black text-danger mb-1 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" /> 고단계 물림 방어 효과</p>
                        <p className="text-[11px] text-muted-foreground font-medium leading-relaxed max-w-[95%]">
                          고단계(6~10단계)에 물렸을 때 무리하게 큰 수익을 노리다가 청산당하는 대신, 본전이나 약익절로 안전하게 탈출하여 계좌의 시드를 완벽하게 보호합니다.
                        </p>
                      </div>

                      <div className="mt-4 p-3.5 bg-primary/[0.02] border border-primary/10 rounded-2xl flex items-start gap-2.5">
                        <Zap className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="text-xs font-black text-foreground">💡 실전 세팅 팁 (ROE 옵션 선택)</p>
                          <p className="text-[11px] text-muted-foreground font-bold leading-relaxed opacity-80">
                            최근 업데이트를 통해 본절청산 목표 ROE를 <span className="text-primary font-black">1.6%, 5%, 3%, 0%</span> 중 선택할 수 있게 되었습니다. 고배율(20배) 운용 시 <span className="text-amber-500 font-black">1.6%나 0%</span> 세팅이 시드 보호에 매우 유리합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'atr_scaling',
                  title: '📈 ATR 동적 물타기 (ATR Dynamic Scaling)',
                  summary: '시장 변동성(ATR) 지표에 비례하여 추매 간격을 자동으로 조절하는 스마트 사다리 기능',
                  content: (
                    <div className="p-6 pt-4 space-y-4 animate-in fade-in-50 duration-200 bg-muted/[0.01]">
                      <div className="space-y-2">
                        <h4 className="text-xs font-black text-foreground">📌 ATR 동적 물타기란?</h4>
                        <p className="text-xs text-muted-foreground font-medium leading-relaxed max-w-[95%]">
                          시장의 실시간 변동성 지표인 <span className="text-primary font-bold">ATR (Average True Range)</span>을 기반으로, DCA 물타기(추매) 간격을 고정된 가격이나 고정 비율이 아닌 시장 변동폭에 비례하여 동적으로 넓히거나 좁히는 스마트 추매 기능입니다.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                        <div className="p-3.5 rounded-2xl bg-muted/5 border border-border">
                          <p className="text-xs font-black text-foreground mb-1 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-danger" /> 급등락/고변동성 장세 (ATR 높음)</p>
                          <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">시장이 거칠게 움직일 때는 물타기 간격을 자동으로 넓혀, 휩쏘에 물량이 순식간에 다 채워져 청산당하는 위험을 완벽히 방지합니다.</p>
                        </div>
                        <div className="p-3.5 rounded-2xl bg-muted/5 border border-border">
                          <p className="text-xs font-black text-foreground mb-1 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-primary" /> 횡보/저변동성 장세 (ATR 낮음)</p>
                          <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">시장이 잔잔할 때는 물타기 간격을 자동으로 좁혀, 얕은 눌림목에서도 촘촘하게 물량을 모으고 빠르게 탈출할 수 있도록 돕습니다.</p>
                        </div>
                      </div>

                      <div className="mt-4 p-3.5 bg-primary/[0.02] border border-primary/10 rounded-2xl flex items-start gap-2.5">
                        <Zap className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="text-xs font-black text-foreground">💡 실전 세팅 팁 (ATR 배수 조절)</p>
                          <p className="text-[11px] text-muted-foreground font-bold leading-relaxed opacity-80">
                            봇 설정에서 <span className="text-primary font-black">ATR 배수(ATR Multiplier)</span>를 조절할 수 있습니다. 기본값(1.5배~2.0배)을 사용하시거나, 변동성이 큰 알트코인의 경우 배수를 높여 방어력을 극대화할 수 있습니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'take_profit',
                  title: '🚀 스마트 익절 전략 (Trailing / Partial / Dynamic TP)',
                  summary: '수익 극대화 및 안전 마진 확보를 위한 익절 모드 안내',
                  content: (
                    <div className="p-6 pt-4 space-y-4 animate-in fade-in-50 duration-200 bg-muted/[0.01]">
                      <div className="space-y-3">
                        <div className="p-3.5 rounded-2xl bg-success/5 border border-success/10">
                          <p className="text-xs font-black text-success mb-1">📈 트레일링 익절 (Trailing TP)</p>
                          <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">
                            수익률이 감시 시작 기준(ROE%)에 도달하면 즉시 익절하지 않고 추적을 개시합니다. 가격이 계속 오르면 익절선을 끌어올리다가, 최고점 대비 설정한 되돌림(콜백) 비율만큼 밀리는 순간 전량 일괄 익절합니다. 소배율 물타기 전략에서 큰 대세 상승을 통째로 익절할 때 가장 유용합니다.
                          </p>
                        </div>
                        <div className="p-3.5 rounded-2xl bg-primary/5 border border-primary/10">
                          <p className="text-xs font-black text-primary mb-1">⚖️ 분할 익절 (Partial TP)</p>
                          <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">
                            목표 익절 지점의 전반부(예: 66%)에 도달했을 때 보유 물량의 절반을 선실현하여 리스크를 줄입니다. 이후 남은 절반은 진입 평단가에 본절 손절(Break-even Cut)을 걸어두고 최종 익절 타겟까지 안전하게 보유합니다.
                          </p>
                        </div>
                        <div className="p-3.5 rounded-2xl bg-amber-500/5 border border-amber-500/10">
                          <p className="text-xs font-black text-amber-600 mb-1">🔄 동적 익절 (Dynamic TP)</p>
                          <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">
                            물타기가 높은 차수까지 깊게 실행되었을 때 작동합니다. 평단가와 가까운 약익절 구간에서 신속하게 전량을 청산하고 탈출할 수 있도록 익절 목표%를 낮춰 계좌 안전을 최우선으로 확보합니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'rebound_reduction',
                  title: '📉 반등 시 물량 덜어내기 (Rebound Reduction)',
                  summary: '추매 진행 중 최저/최고가 대비 반등 시 최근 차수 물량을 선확정 감축하여 탈출을 용이하게 하는 방어 전략',
                  content: (
                    <div className="p-6 pt-4 space-y-4 animate-in fade-in-50 duration-200 bg-muted/[0.01]">
                      <p className="text-xs text-muted-foreground font-medium leading-relaxed">
                        DCA 봇이 설정한 작동 최소 차수(예: 2차 이상)를 넘겨 물타기가 진행되었을 때 작동합니다. 포지션 보유 중 발생한 극값(롱은 최저가, 숏은 최고가)을 실시간 추적하고, 해당 가격 대비 설정 비율(예: 0.5%) 이상 우호적으로 반등하면 최근 진입한 차수 수량만큼 즉시 덜어냅니다.
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                        <div className="p-3.5 rounded-2xl bg-muted/5 border border-border">
                          <p className="text-xs font-black text-foreground mb-1 flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5 text-primary" /> 차수 1단계 롤백</p>
                          <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">성공적으로 덜어내면 현재 물타기 단계가 1회 롤백되어 다음 추가 매수 여력이 생깁니다.</p>
                        </div>
                        <div className="p-3.5 rounded-2xl bg-muted/5 border border-border">
                          <p className="text-xs font-black text-foreground mb-1 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-amber-500" /> 타겟 재정렬</p>
                          <p className="text-[11px] text-muted-foreground font-medium leading-relaxed">덜어낸 반등가를 새로운 기준으로 삼아, 미체결된 하단 물타기 주문들의 타겟 가격을 유리하게 다시 배치합니다.</p>
                        </div>
                      </div>

                      <div className="mt-4 p-3.5 bg-primary/[0.02] border border-primary/10 rounded-2xl flex items-start gap-2.5">
                        <Zap className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div className="space-y-0.5">
                          <p className="text-xs font-black text-foreground">💡 5분봉 3틱룰 & 소배율 물타기 팁</p>
                          <p className="text-[11px] text-muted-foreground font-bold leading-relaxed opacity-80">
                            마틴 배율을 1.0 ~ 1.2배 정도로 극도로 낮게 가져가면서 반등 시 물량 덜어내기를 조합하면, 시드 잠김 현상을 최소화하고 계좌 부담을 덜어내며 장기 보유 국면에서 추세 반전을 안전하게 대기할 수 있습니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                }
              ].map(item => (
                <div key={item.id} className="transition-all">
                  <div
                    className="p-5 flex items-center justify-between cursor-pointer select-none hover:bg-muted/[0.05] transition-all"
                    onClick={() => setOpenHelpId(openHelpId === item.id ? null : item.id)}
                  >
                    <div>
                      <h4 className="text-sm font-black text-foreground flex items-center gap-2">
                        {item.title}
                      </h4>
                      <p className="text-[11px] text-muted-foreground font-medium mt-0.5">{item.summary}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-full hover:bg-muted/20 shrink-0">
                      {openHelpId === item.id ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </Button>
                  </div>
                  {openHelpId === item.id && item.content}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* 포지션 통합 관리 다이얼로그 */}
      {isLiquidateModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm cursor-pointer"
          onClick={(e) => {
            if (!document.body.contains(e.target as Node)) return;
            if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
              setIsLiquidateModalOpen(false);
            }
          }}
        >
          <Card
            ref={modalRef}
            className="w-full max-w-md min-h-[520px] flex flex-col rounded-[32px] shadow-2xl border-none bg-card overflow-hidden cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader className="pb-4">
              <div className="flex justify-between items-center mb-1">
                <Badge variant="outline" className={cx("text-[10px] font-black px-2 py-0.5 rounded-lg border-none", (liquidateSide === 'long' ? "bg-success/10 text-success" : "bg-danger/10 text-danger"))}>
                  {liquidateSide?.toUpperCase()} POSITION
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => setIsLiquidateModalOpen(false)} className="h-8 w-8 p-0 rounded-full hover:bg-muted"><X className="w-4 h-4" /></Button>
              </div>
              <CardTitle className="text-xl font-black flex items-center gap-2">
                포지션 관리
              </CardTitle>
              <CardDescription className="text-xs font-medium">운용 상태 변경 및 포지션 청산을 수행합니다.</CardDescription>
            </CardHeader>
            <CardContent className="max-h-[70vh] overflow-y-auto overflow-x-hidden pr-2 flex-1 scrollbar-thin space-y-4">
              <Tabs value={modalTab} onValueChange={setModalTab} className="w-full">
                <TabsList className="grid grid-cols-3 w-full mb-4 bg-muted/20 p-1 rounded-xl">
                  <TabsTrigger value="control" className="text-xs font-bold py-2 rounded-lg data-[state=active]:bg-card data-[state=active]:text-foreground">운영 & 청산</TabsTrigger>
                  <TabsTrigger value="tp" className="text-xs font-bold py-2 rounded-lg data-[state=active]:bg-card data-[state=active]:text-foreground">개별 익절(TP) 설정</TabsTrigger>
                  <TabsTrigger value="sl" className="text-xs font-bold py-2 rounded-lg data-[state=active]:bg-card data-[state=active]:text-foreground">개별 손절(SL) 설정</TabsTrigger>
                </TabsList>

                <TabsContent value="control" className="space-y-6 mt-0">
                  {/* 운용 상태 섹션 */}
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                      <Activity className="w-3 h-3" /> 운용 상태 설정
                    </label>
                    <div className="grid grid-cols-1 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => { handleResumeAll(liquidateSide!); }}
                        className={cx(
                          "h-14 rounded-2xl flex flex-col items-start px-4 gap-0.5 justify-center transition-all border-2",
                          !bot.paused_buy_sides?.includes(liquidateSide) && !bot.suspended_sides?.includes(liquidateSide)
                            ? "bg-success/5 border-success/30 text-success shadow-sm"
                            : "bg-muted/30 border-border opacity-60 text-muted-foreground"
                        )}
                      >
                        <div className="flex items-center gap-1.5">
                          <Zap className={cx("w-3.5 h-3.5", !bot.paused_buy_sides?.includes(liquidateSide) && !bot.suspended_sides?.includes(liquidateSide) && "fill-current")} />
                          <span className="text-xs font-black">정상 운영 (Normal)</span>
                        </div>
                        <span className="text-[9px] font-bold opacity-60">진입 및 DCA 추매가 활발히 진행됩니다.</span>
                      </Button>

                      <Button
                        variant="outline"
                        onClick={() => { handlePauseBuy(liquidateSide!); }}
                        className={cx(
                          "h-14 rounded-2xl flex flex-col items-start px-4 gap-0.5 justify-center transition-all border-2",
                          bot.paused_buy_sides?.includes(liquidateSide)
                            ? "bg-amber-500/10 border-amber-500/30 text-amber-600 shadow-sm"
                            : "bg-muted/30 border-border opacity-60 text-muted-foreground"
                        )}
                      >
                        <div className="flex items-center gap-1.5">
                          <Pause className={cx("w-3.5 h-3.5", bot.paused_buy_sides?.includes(liquidateSide) && "fill-current")} />
                          <span className="text-xs font-black">추매 일시정지 (Scaling Paused)</span>
                        </div>
                        <span className="text-[9px] font-bold opacity-60">현재 물량은 유지하되 추가 물타기를 하지 않습니다.</span>
                      </Button>

                      <Button
                        variant="outline"
                        onClick={() => { handleToggleStopAtTp(liquidateSide!); }}
                        className={cx(
                          "h-14 rounded-2xl flex flex-col items-start px-4 gap-0.5 justify-center transition-all border-2",
                          bot.stop_at_tp_sides?.includes(liquidateSide)
                            ? "bg-danger/10 border-danger/30 text-danger shadow-sm"
                            : "bg-muted/30 border-border opacity-60 text-muted-foreground"
                        )}
                      >
                        <div className="flex items-center gap-1.5">
                          <Square className={cx("w-3.5 h-3.5", bot.stop_at_tp_sides?.includes(liquidateSide) && "fill-current")} />
                          <span className="text-xs font-black">청산 후 진입 일시 정지 (Suspend after Cycle)</span>
                        </div>
                        <span className="text-[9px] font-bold opacity-60">이번 사이클이 종료(익절/손절/청산)되면 자동으로 <br />해당 사이드의 진입을 일시 정지합니다.</span>
                      </Button>
                    </div>
                  </div>

                  {activeDetails.some((c: any) => c.side === liquidateSide) && (
                    <>
                      <div className="h-px bg-border/50 mx-[-24px]" />

                      {/* 청산 섹션 */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-end">
                          <label className="text-[11px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                            <Trash2 className="w-3 h-3" /> 수동 포지션 청산
                          </label>
                          <span className="text-[11px] font-black text-primary">{liquidatePercent}% 선택됨</span>
                        </div>

                        <div className="grid grid-cols-4 gap-2">
                          {[25, 50, 75, 100].map(pct => (
                            <Button
                              key={pct}
                              variant="outline"
                              size="sm"
                              onClick={() => setLiquidatePercent(pct)}
                              className={cx(
                                "h-9 rounded-xl font-black text-xs transition-all",
                                liquidatePercent === pct ? "bg-primary text-white border-primary shadow-md shadow-primary/20" : "bg-muted/10 border-border hover:bg-muted/30"
                              )}
                            >
                              {pct}%
                            </Button>
                          ))}
                        </div>

                        <div className="relative mt-2">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={liquidatePercent}
                            onChange={(e) => setLiquidatePercent(Math.min(100, Math.max(1, Number(e.target.value))))}
                            className="w-full h-10 rounded-xl border border-border bg-muted/5 pl-4 pr-12 text-sm font-black focus:ring-2 focus:ring-primary/20 focus:border-primary/50 focus:outline-none transition-all"
                            placeholder="직접 입력"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-black text-muted-foreground">%</span>
                        </div>

                        {liquidatePercent >= 100 && (
                          <div className="flex items-start space-x-3 space-y-0 p-3 bg-primary/[0.02] rounded-2xl border border-primary/10 transition-all">
                            <Checkbox id="suspend" checked={shouldSuspend} onCheckedChange={(val: any) => setShouldSuspend(!!val)} className="mt-0.5 border-primary/30 data-[state=checked]:bg-primary" />
                            <div className="grid gap-1 leading-none">
                              <label htmlFor="suspend" className="text-[11px] font-black leading-none cursor-pointer text-primary/80">
                                청산 후 재진입 방지 (Suspend)
                              </label>
                              <p className="text-[9px] text-muted-foreground font-bold opacity-60">
                                봇이 해당 방향에 다시 진입하지 않도록 잠급니다.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </TabsContent>

                <TabsContent value="sl" className="space-y-6 mt-0">
                  {/* 손절 가격 커스터마이즈 섹션 */}
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-muted-foreground uppercase tracking-widest flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5 text-danger" /> 개별 손절 비율 설정 (Stop Loss ROE%)
                      </span>
                      {bot.settings?.[`${liquidateSide?.toLowerCase()}_stop_loss_pct`] === -1 && (
                        <Badge className="bg-danger/10 text-danger border-none text-[8px] font-black h-4 px-1.5">
                          손절 OFF
                        </Badge>
                      )}
                    </label>
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="number"
                          step="any"
                          value={customStopLoss === '-1' ? '' : customStopLoss}
                          onChange={(e) => setCustomStopLoss(e.target.value)}
                          className="text-[10px] w-full h-10 rounded-xl border border-border bg-muted/5 pl-4 pr-24 text-sm font-black focus:ring-2 focus:ring-primary/20 focus:border-primary/50 focus:outline-none transition-all"
                          placeholder={bot.settings?.[`${liquidateSide?.toLowerCase()}_stop_loss_pct`] === -1 ? "손절 OFF 상태" : "기본값 사용 (0: 초기화 / -1: 끄기)"}
                        />
                        <span className="absolute right-16 top-1/2 -translate-y-1/2 text-[11px] font-black text-muted-foreground">%</span>
                        <Button
                          size="sm"
                          onClick={handleUpdateCustomSl}
                          className="absolute right-1 top-1 h-8 rounded-lg font-black text-[11px] bg-primary text-white hover:bg-primary/95"
                        >
                          설정
                        </Button>
                      </div>
                      <p className="text-[9px] text-muted-foreground font-bold opacity-60">
                        전역 손절 설정: {bot.sl_pct || 0}%
                      </p>
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCustomStopLoss('-1');
                            router.post(`/dca/${bot.id}/update_custom_sl`, {
                              side: liquidateSide,
                              stop_loss_pct: '-1'
                            }, {
                              preserveScroll: true,
                              onSuccess: () => {
                                setIsLiquidateModalOpen(false);
                                router.reload({ only: ['bot'] });
                              }
                            });
                          }}
                          className="flex-1 h-8 rounded-xl text-[10px] font-black border-danger/30 text-danger hover:bg-danger/10"
                        >
                          손절 비활성화 (OFF)
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCustomStopLoss('0');
                            router.post(`/dca/${bot.id}/update_custom_sl`, {
                              side: liquidateSide,
                              stop_loss_pct: '0'
                            }, {
                              preserveScroll: true,
                              onSuccess: () => {
                                setIsLiquidateModalOpen(false);
                                router.reload({ only: ['bot'] });
                              }
                            });
                          }}
                          className="flex-1 h-8 rounded-xl text-[10px] font-black text-muted-foreground hover:bg-muted/10 border-border"
                        >
                          기본값으로 복원
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                <TabsContent value="tp" className="space-y-6 mt-0">
                  <div className="space-y-3">
                    <label className="text-[11px] font-black text-muted-foreground uppercase flex items-center gap-1.5">
                      <Info className="w-3.5 h-3.5 text-success" /> 익절 ROE % (음수 가능)
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        value={customTpPct}
                        onChange={e => setCustomTpPct(e.target.value)}
                        className="text-[10px] w-full h-10 rounded-xl border border-border bg-muted/5 pl-4 pr-24 text-sm font-black focus:ring-2 focus:ring-primary/20 focus:border-primary/50"
                        placeholder="예: -5 (음수) 또는 5"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-black text-success">%</span>
                      <Button
                        size="sm"
                        onClick={handleUpdateCustomTp}
                        className="absolute right-1 top-1 h-8 rounded-lg font-black text-[11px] bg-primary text-white hover:bg-primary/95"
                      >설정</Button>
                    </div>
                    <p className="text-[9px] text-muted-foreground font-bold opacity-60">
                      전역 TP 설정: {bot.tp_pct || 0}%
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
            {modalTab === 'control' && activeDetails.some((c: any) => c.side === liquidateSide) && (
              <CardFooter className="flex gap-3 px-6 pb-6 pt-0 shrink-0">
                <Button
                  variant="outline"
                  disabled={isAnyLiquidating}
                  onClick={confirmLiquidateSide}
                  className="flex-1 h-12 rounded-2xl font-black border-primary/30 text-primary bg-primary/[0.02] hover:bg-primary hover:text-white transition-all shadow-none"
                >
                  {isLiquidatingSide ? <Loader2 className="w-4 h-4 animate-spin" /> : (liquidatePercent >= 100 ? '전체 청산 실행' : `${liquidatePercent}% 부분 청산`)}
                </Button>
              </CardFooter>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};
